import { responsiveFontSizes } from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import { withStyles } from '@mui/styles';

// Colors.
const grey100 = '#f5f6fa';
const grey200 = '#d5d5d5';
const grey600 = '#dddee5';
const grey700 = '#343a40';
const grey800 = '#24282c';
const primary700 = '#6576ff';
const primary800 = '#002884';
const warning600 = '#e9bc35';
const warning700 = '#ffc107';
const warning800 = '#ff9800';
const success600 = '#7fc881';
const success700 = '#44cd48';
const success800 = '#269129';
const danger600 = '#e56c6c';
const danger700 = '#d32f2f';
const danger800 = '#c90a0a';

export const colorDun1 = '#284dd0';
export const colorDun2 = '#249d2d';
export const colorDun3 = '#be99fc';
export const colorDun4 = '#40e7e6';
export const colorDun5 = '#8a8131';
export const colorDun6 = '#ff9b00';
export const colorDunCover = '#f500cd';
export const colorDunCover2 = '#7000f8';

// Spacing.
const spacing = 6;

// Border.
const borderWidth = 1;
const borderColor = grey100;

// Breakpoints.
const xl = 1920;
const lg = 1280;
const md = 960;
const sm = 600;
const xs = 0;

const ThemeStyles = withStyles({
    '@global': {
        html: {
            margin: 0,
            height: '100%',
        },
        body: {
            minWidth: md,
            overflow: 'hidden',
            height: '100%',
            margin: 0,
            padding: 0,
            backgroundColor: grey100,
        },
        '#root': {
            height: '100%',
        },
        '*, *:focus-visible': {
            outline: 'none',
        },
        '*, *::before, *::after': {
            boxSizing: 'border-box',
        },
    },
})(() => null);

const theme = responsiveFontSizes(
    createTheme({
        palette: {
            primary: {
                main: primary700,
                dark: primary800,
            },
            grey: {
                main: grey700,
                light: grey600,
                dark: grey800,
            },
            warning: {
                light: warning600,
                main: warning700,
                dark: warning800,
            },
            success: {
                light: success600,
                main: success700,
                dark: success800,
            },
            danger: {
                light: danger600,
                main: danger700,
                dark: danger800,
            },
            text: {
                secondary: 'rgba(0, 0, 0, 0.54)',
            },

            // Used to shift a color's luminance by approximately
            // two indexes within its tonal palette.
            // E.g., shift from Red 500 to Red 300 or Red 700.
            tonalOffset: 0.2,
            background: {
                paper: '#FFFFFF',
                default: grey100,
            },
            divider: '#efefef',
        },
        spacing: spacing,
        shape: {
            borderRadius: 2,
        },
        breakpoints: {
            // Define custom breakpoint values.
            // These will apply to Material-UI components that use responsive
            // breakpoints, such as `Grid` and `Hidden`. You can also use the
            // theme breakpoint functions `up`, `down`, and `between` to create
            // media queries for these breakpoints
            values: {
                xl,
                lg,
                md,
                sm,
                xs,
            },
        },
        border: {
            borderColor: alpha(grey700, 0.12),
            borderWidth: borderWidth,
        },
        zIndex: {
            navbar: 1099,
            sidebar: 1100,
            notistack: 1101,
        },
        components: {
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                    },
                },
            },
            MuiExpansionPanel: {
                styleOverrides: {
                    root: {
                        position: 'static',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        lineHeight: '1.2',
                        paddingLeft: 8 * 2,
                        paddingRight: 8 * 2,
                        borderBottom: `${borderWidth}px solid ${borderColor}`,
                        [`@media (max-width:  ${sm}px)`]: {
                            paddingLeft: 8,
                            paddingRight: 8,
                        },
                    },
                },
            },
            MuiPrivateNotchedOutline: {
                root: {
                    borderWidth: borderWidth,
                },
            },
            MuiListItem: {
                styleOverrides: {
                    divider: {
                        borderBottom: `${borderWidth}px solid ${borderColor}`,
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    root: {
                        boxShadow: '0 1px 3px ' + alpha(grey700, 0.12),
                        '&:active': {
                            boxShadow: '0 1px 3px ' + alpha(grey700, 0.12),
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        '.simplebar-container': {
                            display: 'flex',
                            overflowY: 'auto',
                        },
                        '.MuiDialogTitle-root': {
                            '.MuiTypography-root': {
                                fontSize: '13px',
                            },
                        },
                        '.MuiDialogContent-root': {
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    },
                    paper: {
                        width: '100%',
                        maxWidth: 430,
                        marginLeft: 8,
                        marginRight: 8,
                        borderRadius: 4,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '12px',
                        borderRadius: 4,
                        backgroundColor: grey800,
                    },
                    arrow: {
                        color: grey800,
                    },
                },
            },
            MuiExpansionPanelDetails: {
                root: {
                    [`@media (max-width:  ${sm}px)`]: {
                        paddingLeft: 8,
                        paddingRight: 8,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        '.MuiSelect-select': {
                            height: '1.385em !important',
                            minHeight: '1.385em !important',
                        },
                    },
                    icon: {
                        fontSize: '18px',
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                        borderRadius: '4px !important',
                        '.MuiOutlinedInput-input': {
                            padding: '14px 14px',
                        },
                        '.MuiOutlinedInput-input:-internal-autofill-selected': {
                            WebkitBackgroundClip: 'text',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            top: '-8px',
                            borderWidth: `${borderWidth}px !important`,
                        },
                        '.MuiOutlinedInput-notchedOutline legend': {
                            height: '16px',
                            fontSize: '0.87em',
                        },
                        '.MuiInputAdornment-root .MuiButtonBase-root': {
                            marginRight: '-4px',
                        },
                        '&:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${grey200}`,
                        },
                    },
                    input: {
                        height: '1.385em',
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                        '&:not(.MuiInputLabel-shrink)': {
                            transform: 'translate(12px, 14px) scale(1)',
                        },
                        '&.Mui-focused, &.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
                            fontSize: '14px',
                            lineHeight: '1.6375em',
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginLeft: spacing / 3,
                        marginRight: spacing / 3,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                        borderRadius: '4px',
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        '.MuiTouchRipple-rippleVisible': {
                            color: alpha(grey700, 0.12),
                        },
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        border: '0',
                        overflow: 'visible',
                        boxShadow: '0 1px 3px ' + alpha(grey700, 0.12),
                        '&:before': {
                            top: 0,
                            width: 10,
                            right: 14,
                            height: 10,
                            zIndex: 0,
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            transform: 'translateY(-50%) rotate(45deg)',
                            boxShadow: '-2px -2px 8px -2px ' + alpha(grey700, 0.12),
                            backgroundColor: '#fff',
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontSize: '13px',
                    },
                },
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        '.MuiListItem-root': {
                            '.MuiListItemButton-root': {
                                transition: 'none',
                            },
                        },
                        '.MuiListItem-root.Mui-selected': {
                            backgroundColor: 'transparent',
                            '.MuiListItemButton-root': {
                                color: '#FFFFFF',
                                backgroundColor: primary700,
                                '.MuiAvatar-circular': {
                                    backgroundColor: '#FFFFFF',
                                },
                            },
                        },
                        '.MuiListItemSecondaryAction-root': {
                            right: spacing * 2,
                        },
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                        paddingLeft: spacing * 2,
                        paddingRight: spacing * 2,
                        '&.MuiButtonBase-root.Mui-selected': {
                            color: '#FFFFFF',
                            backgroundColor: primary700,
                        },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            backgroundColor: grey100,
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: alpha(grey700, 0.12),
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    root: {
                        '.MuiPaper-root': {
                            borderRadius: '4px',
                        },
                        '.MuiList-root': {
                            padding: '4px 0',
                            overflow: 'hidden',
                            borderRadius: '4px',
                        },
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        width: '32px',
                        height: '32px',
                        img: {
                            width: '100%',
                            height: '100%',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        color: '#000000',
                    },
                    elevation1: {
                        border: '0',
                        bgcolor: 'background.paper',
                        boxShadow: '0px 1px 2px ' + alpha(grey700, 0.12),
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    message: {
                        fontSize: '12px',
                    },
                },
            },
        },
        typography: {
            useNextVariants: true,
            fontFamily: `"Roboto", sans-serif`,
        },
    })
);

export { theme, ThemeProvider, ThemeStyles };
